import { createContext, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft } from "lucide-react";

// Drop-in replacement for the shadcn Sheet: renders the panel INSIDE the Observations column
// (portal into #side-panel-slot), replacing the list, with a Back button — it never covers the map.
const Ctx = createContext({ open: false, onOpenChange: () => {} });
const SLOT_ID = "side-panel-slot";

export const Sheet = ({ open, onOpenChange, children }) => (
  <Ctx.Provider value={{ open, onOpenChange }}>{children}</Ctx.Provider>
);

const stripWidth = (cls = "") => cls.replace(/\b(w-full|sm:max-w-\S+|border-l)\b/g, "");

export const SheetContent = ({ className, children, side, ...rest }) => {
  const { open, onOpenChange } = useContext(Ctx);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onOpenChange(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);
  const slot = typeof document !== "undefined" ? document.getElementById(SLOT_ID) : null;
  if (!open || !slot) return null;
  return createPortal(
    <section
      {...rest}
      className={`absolute inset-0 z-20 flex flex-col overflow-y-auto atlas-side-in ${stripWidth(className)}`}
    >
      {children}
    </section>,
    slot,
  );
};

export const SheetHeader = ({ className = "", children }) => {
  const { onOpenChange } = useContext(Ctx);
  return (
    <header className="shrink-0 sticky top-0 z-10">
      <div className="h-9 px-4 flex items-center border-b border-white/10 bg-atlas-surface">
        <button
          onClick={() => onOpenChange(false)}
          data-testid="side-panel-back"
          className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-atlas-secondary hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to incidents
        </button>
      </div>
      <div className={className.replace(/\bsticky top-0\b/, "")}>{children}</div>
    </header>
  );
};

export const SheetTitle = ({ className = "", children }) => <h2 className={className}>{children}</h2>;
export const SIDE_PANEL_SLOT = SLOT_ID;
